import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    BarChart3,
    TrendingUp,
    DollarSign,
    Users,
    Eye,
    MousePointer,
    Target,
    RefreshCw,
    Loader2,
    ArrowUpRight,
    ArrowDownRight,
    Minus,
    Settings,
    Download,
    Calendar,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logger from '@/lib/logger';

function MetricCard({ title, value, prefix = '', suffix = '', icon: Icon, color = 'text-primary', loading }) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground">{title}</p>
                        {loading ? (
                            <div className="h-8 w-24 bg-muted animate-pulse rounded mt-1" />
                        ) : (
                            <p className={`text-2xl font-bold ${color}`}>
                                {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
                            </p>
                        )}
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                        <Icon className={`h-5 w-5 ${color}`} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function MarketingDashboardPage() {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState('all');
    const [datePreset, setDatePreset] = useState('last_30d');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount, datePreset, customStartDate, customEndDate]);

    const fetchDashboard = async () => {
        try {
            const params = new URLSearchParams({ date_preset: datePreset });
            if (selectedAccount !== 'all') {
                params.append('account_id', selectedAccount);
            }
            if (datePreset === 'custom' && customStartDate && customEndDate) {
                params.append('start_date', customStartDate);
                params.append('end_date', customEndDate);
            }
            
            const response = await api.get(`/marketing/dashboard?${params.toString()}`);
            setDashboard(response.data);
        } catch (error) {
            logger.error('Error fetching dashboard:', error);
            if (error.response?.status !== 403) {
                toast.error('Failed to load dashboard data');
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchDashboard();
    };

    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case 'ACTIVE':
                return <Badge className="bg-green-500">Active</Badge>;
            case 'PAUSED':
            case 'CAMPAIGN_PAUSED':
                return <Badge variant="secondary">Paused</Badge>;
            case 'ARCHIVED':
                return <Badge variant="outline">Archived</Badge>;
            default:
                return <Badge variant="outline">{status || 'Unknown'}</Badge>;
        }
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return 'AED 0.00';
        return `AED ${Number(value || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatNumber = (value) => {
        return new Intl.NumberFormat('en-US').format(value || 0);
    };

    const getRoiBadgeColor = (roi) => {
        if (roi > 500) return 'bg-green-500 text-white';
        if (roi > 100) return 'bg-green-400 text-white';
        if (roi > 0) return 'bg-yellow-400 text-black';
        return 'bg-red-400 text-white';
    };

    const summary = dashboard?.summary || {};
    const campaigns = dashboard?.campaigns || [];
    const accounts = dashboard?.accounts || [];

    // Filter campaigns by status
    const filteredCampaigns = useMemo(() => {
        let filtered = [...campaigns];
        if (statusFilter !== 'all') {
            filtered = filtered.filter(c => c.status?.toUpperCase() === statusFilter.toUpperCase());
        }
        return filtered.sort((a, b) => (b.spend || 0) - (a.spend || 0));
    }, [campaigns, statusFilter]);

    // Status counts for filter badges
    const statusCounts = useMemo(() => {
        const counts = { all: campaigns.length };
        campaigns.forEach(c => {
            const s = (c.status || 'UNKNOWN').toUpperCase();
            counts[s] = (counts[s] || 0) + 1;
        });
        return counts;
    }, [campaigns]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="marketing-dashboard-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Marketing Analytics</h1>
                    <p className="text-muted-foreground">Track your Meta Ads performance (AED)</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-3">
                    <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                        <SelectTrigger className="w-[200px]" data-testid="account-filter">
                            <SelectValue placeholder="All Accounts" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Accounts</SelectItem>
                            {accounts.map(account => (
                                <SelectItem key={account.id} value={account.id}>
                                    {account.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    <Select value={datePreset} onValueChange={(val) => { setDatePreset(val); if (val !== 'custom') { setCustomStartDate(''); setCustomEndDate(''); } }}>
                        <SelectTrigger className="w-[160px]" data-testid="date-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="last_7d">Last 7 days</SelectItem>
                            <SelectItem value="last_14d">Last 14 days</SelectItem>
                            <SelectItem value="last_30d">Last 30 days</SelectItem>
                            <SelectItem value="this_month">This Month</SelectItem>
                            <SelectItem value="last_month">Last Month</SelectItem>
                            <SelectItem value="last_90d">Last 3 Months</SelectItem>
                            <SelectItem value="last_180d">Last 6 Months</SelectItem>
                            <SelectItem value="maximum">All Time</SelectItem>
                            <SelectItem value="custom">Custom Range</SelectItem>
                        </SelectContent>
                    </Select>
                    
                    <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-dashboard-btn">
                        <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    
                    <Button variant="outline" onClick={() => navigate('/marketing/settings')} data-testid="settings-btn">
                        <Settings className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Custom Date Range */}
            {datePreset === 'custom' && (
                <Card>
                    <CardContent className="p-4 flex flex-wrap items-end gap-4">
                        <div>
                            <Label htmlFor="start-date" className="text-xs text-muted-foreground">Start Date</Label>
                            <Input id="start-date" type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="w-[180px]" data-testid="custom-start-date" />
                        </div>
                        <div>
                            <Label htmlFor="end-date" className="text-xs text-muted-foreground">End Date</Label>
                            <Input id="end-date" type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="w-[180px]" data-testid="custom-end-date" />
                        </div>
                        <Button onClick={handleRefresh} disabled={!customStartDate || !customEndDate} size="sm" data-testid="apply-custom-date-btn">
                            <Calendar className="h-4 w-4 mr-1" /> Apply
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* No Accounts Warning */}
            {accounts.length === 0 && (
                <Card className="border-yellow-500/50 bg-yellow-500/5">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-full bg-yellow-500/10"><Target className="h-6 w-6 text-yellow-500" /></div>
                            <div className="flex-1">
                                <h3 className="font-semibold">No Meta Accounts Connected</h3>
                                <p className="text-sm text-muted-foreground">Connect your Meta Ads accounts to start tracking campaign performance.</p>
                            </div>
                            <Button onClick={() => navigate('/marketing/settings')}>Connect Account</Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Summary Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                <MetricCard title="Total Spend" value={summary.total_spend || 0} prefix="AED " icon={DollarSign} color="text-red-500" loading={refreshing} />
                <MetricCard title="Meta Leads" value={summary.total_leads || 0} icon={Users} color="text-green-500" loading={refreshing} />
                <MetricCard title="Cost Per Lead" value={summary.cpl || 0} prefix="AED " icon={Target} color="text-blue-500" loading={refreshing} />
                <MetricCard title="Enrolled" value={summary.total_enrolled || 0} icon={TrendingUp} color="text-emerald-500" loading={refreshing} />
                <MetricCard title="Revenue" value={summary.total_revenue || 0} prefix="AED " icon={DollarSign} color="text-green-600" loading={refreshing} />
                <MetricCard title="ROI" value={summary.total_spend > 0 ? `${summary.overall_roi}%` : 'N/A'} icon={TrendingUp} color={summary.overall_roi > 0 ? 'text-green-500' : 'text-red-500'} loading={refreshing} />
                <MetricCard title="Impressions" value={summary.total_impressions || 0} icon={Eye} color="text-purple-500" loading={refreshing} />
                <MetricCard title="CTR" value={summary.ctr || 0} suffix="%" icon={MousePointer} color="text-cyan-500" loading={refreshing} />
            </div>

            {/* Additional Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">CPM</p><p className="text-xl font-bold">{formatCurrency(summary.cpm)}</p></div><Badge variant="outline">per 1K</Badge></div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">CPC</p><p className="text-xl font-bold">{formatCurrency(summary.cpc)}</p></div><Badge variant="outline">per click</Badge></div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Reach</p><p className="text-xl font-bold">{formatNumber(summary.total_reach)}</p></div><Badge variant="outline">unique</Badge></div></CardContent></Card>
                <Card><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Frequency</p><p className="text-xl font-bold">{(summary.frequency || 0).toFixed(2)}x</p></div><Badge variant="outline">avg</Badge></div></CardContent></Card>
            </div>

            {/* Campaigns Table with ROI */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="h-5 w-5" />
                            Campaign Performance & ROI
                        </CardTitle>
                        <CardDescription>
                            {filteredCampaigns.length} of {campaigns.length} campaigns — Enrolled & Revenue attributed proportionally by lead count
                        </CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {[
                            { value: 'all', label: 'All' },
                            { value: 'ACTIVE', label: 'Active' },
                            { value: 'PAUSED', label: 'Paused' },
                            { value: 'ARCHIVED', label: 'Archived' },
                        ].map(f => (
                            <Button key={f.value} variant={statusFilter === f.value ? 'default' : 'ghost'} size="sm" onClick={() => setStatusFilter(f.value)} data-testid={`status-filter-${f.value}`}>
                                {f.label} {statusCounts[f.value] ? `(${statusCounts[f.value]})` : ''}
                            </Button>
                        ))}
                    </div>
                </CardHeader>
                <CardContent>
                    {filteredCampaigns.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No campaigns found</p>
                            <p className="text-sm">{campaigns.length === 0 ? 'Sync your accounts to see campaign data' : 'No campaigns match the selected filter'}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Campaign</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Spend (AED)</TableHead>
                                        <TableHead className="text-right">Leads</TableHead>
                                        <TableHead className="text-right">CPL (AED)</TableHead>
                                        <TableHead className="text-right">Impressions</TableHead>
                                        <TableHead className="text-right">CTR</TableHead>
                                        <TableHead className="text-right bg-green-500/5">Enrolled</TableHead>
                                        <TableHead className="text-right bg-green-500/5">Revenue (AED)</TableHead>
                                        <TableHead className="text-right bg-green-500/5">ROI</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredCampaigns.map((campaign) => (
                                        <TableRow key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium text-sm">{campaign.name}</p>
                                                    <p className="text-xs text-muted-foreground">{campaign.objective || 'N/A'}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                                            <TableCell className="text-right font-medium">{formatCurrency(campaign.spend)}</TableCell>
                                            <TableCell className="text-right"><span className="font-medium text-blue-500">{campaign.leads || 0}</span></TableCell>
                                            <TableCell className="text-right">{formatCurrency(campaign.cpl)}</TableCell>
                                            <TableCell className="text-right">{formatNumber(campaign.impressions)}</TableCell>
                                            <TableCell className="text-right">{(campaign.ctr || 0).toFixed(2)}%</TableCell>
                                            <TableCell className="text-right bg-green-500/5">
                                                <span className="font-semibold text-green-600">{campaign.enrolled_attributed || 0}</span>
                                            </TableCell>
                                            <TableCell className="text-right bg-green-500/5">
                                                <span className="font-semibold text-green-600">{formatCurrency(campaign.revenue_attributed)}</span>
                                            </TableCell>
                                            <TableCell className="text-right bg-green-500/5">
                                                {campaign.spend > 0 ? (
                                                    <Badge className={getRoiBadgeColor(campaign.roi)}>
                                                        {campaign.roi > 0 ? '+' : ''}{campaign.roi}%
                                                    </Badge>
                                                ) : (
                                                    <span className="text-muted-foreground text-xs">-</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* DB Leads Info */}
            {summary.db_leads > 0 && (
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-500/10"><Download className="h-5 w-5 text-blue-500" /></div>
                                <div>
                                    <p className="font-medium">Leads in Database</p>
                                    <p className="text-sm text-muted-foreground">{summary.db_leads} leads received via webhook</p>
                                </div>
                            </div>
                            <Button variant="outline" onClick={() => navigate('/marketing/leads')}>View Leads</Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
